'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';

import { turnstileHeaders } from '@cloudquery/custom-fetch';
import { getEnv } from 'shared/base';

interface TurnstileContextValue {
  clearToken: () => void;
  hasToken: boolean;
  setToken: (token: string | null) => void;
  siteKey: string;
  token: string | null;
}

export const TurnstileContext = createContext<TurnstileContextValue | null>(null);

export const TURNSTILE_TOKEN_LOCALSTORAGE_KEY = 'turnstile-token';

const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(TURNSTILE_TOKEN_LOCALSTORAGE_KEY);
    if (!stored || stored === 'null' || stored === 'undefined') {
      localStorage.removeItem(TURNSTILE_TOKEN_LOCALSTORAGE_KEY);

      return null;
    }

    return stored;
  } catch {
    return null;
  }
};

const setStoredToken = (token: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      localStorage.setItem(TURNSTILE_TOKEN_LOCALSTORAGE_KEY, token);
    } else {
      localStorage.removeItem(TURNSTILE_TOKEN_LOCALSTORAGE_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
};

const setTurnstileHeaders = (siteKey: string, token: string) => {
  turnstileHeaders['X-Turnstile-Site-Key'] = siteKey;
  turnstileHeaders['X-Turnstile-Response'] = token;
};

const clearTurnstileHeaders = () => {
  delete turnstileHeaders['X-Turnstile-Site-Key'];
  delete turnstileHeaders['X-Turnstile-Response'];
};

export function TurnstileProvider({
  children,
  siteKey: siteKeyProp,
}: {
  children: ReactNode;
  siteKey?: string;
}) {
  const siteKey = useMemo(
    () =>
      siteKeyProp ||
      getEnv('NEXT_PUBLIC_TURNSTILE_SITE_KEY', process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY, '') ||
      '',
    [siteKeyProp],
  );

  const storedToken = useMemo(() => getStoredToken(), []);
  const [token, setTokenState] = useState<string | null>(storedToken);

  useEffect(() => {
    if (storedToken && storedToken !== 'null' && siteKey) {
      setTurnstileHeaders(siteKey, storedToken);
    } else {
      clearTurnstileHeaders();
    }

    return () => {
      clearTurnstileHeaders();
    };
  }, [storedToken, siteKey]);

  const setToken = useCallback(
    (newToken: string | null) => {
      if (newToken && siteKey) {
        setTurnstileHeaders(siteKey, newToken);
        setTokenState(newToken);
        setStoredToken(newToken);
      } else {
        clearTurnstileHeaders();
        setTokenState(null);
        setStoredToken(null);
      }
    },
    [siteKey],
  );

  const clearToken = useCallback(() => {
    clearTurnstileHeaders();
    setTokenState(null);
    setStoredToken(null);
  }, []);

  const hasToken = !!token;

  return (
    <TurnstileContext.Provider value={{ clearToken, hasToken, setToken, siteKey, token }}>
      {children}
    </TurnstileContext.Provider>
  );
}

export function useTurnstile() {
  const context = useContext(TurnstileContext);
  if (!context) {
    throw new Error('useTurnstile must be used within TurnstileProvider');
  }

  return context;
}
